import { useTranslation } from 'react-i18next';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import AnswerWidget from './AnswerWidget';

// Columns by the grid's own width (the chat pane, not the window): a quarter
// card stays wide enough for a KPI tile.
const COLUMNS_FROM = [
  [780, 4],
  [420, 2],
];

/** 4 columns on a wide grid, 2 on a medium one, 1 on a narrow one. */
export function gridColumns(width) {
  const found = COLUMNS_FROM.find(([min]) => width >= min);
  return found ? found[1] : 1;
}

const SPANS = {
  4: { full: 4, half: 2, quarter: 1 },
  2: { full: 2, half: 2, quarter: 1 },
  1: { full: 1, half: 1, quarter: 1 },
};

/** How many of `columns` a card of `width` ('full' | 'half' | 'quarter') spans. */
export function cardSpan(width, columns) {
  return SPANS[columns]?.[width] || columns;
}

/** The element's content width, kept current while it resizes. */
function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // ResizeObserver supplies the initial size as well as later changes;
    // reading layout here would serialize every mounted dashboard grid.
    let appliedWidth = typeof ResizeObserver === 'undefined' ? el.getBoundingClientRect().width : 0;
    let pendingWidth = appliedWidth;
    let frame = null;
    let closed = false;
    setWidth(appliedWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      if (closed) return;
      pendingWidth = entry.contentRect.width;
      if (frame !== null || pendingWidth === appliedWidth) return;
      // A synchronous grid update changes card heights during observer
      // delivery. Apply the latest width in a frame after delivery completes.
      frame = requestAnimationFrame(() => {
        frame = null;
        if (closed || pendingWidth === appliedWidth) return;
        appliedWidth = pendingWidth;
        setWidth(appliedWidth);
      });
    });
    observer.observe(el);
    return () => {
      closed = true;
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [ref]);
  return width;
}

/**
 * An answer's widgets as the model laid them out: in its order, each card as
 * wide as it asked, on a grid of as many columns as the pane fits.
 */
export default React.memo(function WidgetGrid({ messageId, items, filters, renderWidget, onReply }) {
  const { t: translate } = useTranslation();
  const ref = useRef(null);
  const columns = gridColumns(useWidth(ref));
  return (
    <Box
      ref={ref}
      component="ul"
      role="list"
      aria-label={translate('assistant.widgetGridResults')}
      sx={{
        listStyle: 'none',
        m: 0,
        mt: 1.5,
        p: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: 2,
      }}
    >
      {items.map(({ index, width, widget }) => (
        <Box
          component="li"
          key={index}
          data-width={width}
          sx={{ gridColumn: `span ${cardSpan(width, columns)}`, minWidth: 0, display: 'flex' }}
        >
          {renderWidget
            ? renderWidget({ index, widget, width, filters: filters?.[index] })
            : <AnswerWidget messageId={messageId} index={index} widget={widget} width={width} filters={filters?.[index]} onReply={onReply} />}
        </Box>
      ))}
    </Box>
  );
});
